import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { motion } from "framer-motion";
import { Instagram, MessageCircle, Send, Sparkles, Bot, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function stageBadge(stage) {
  const map = {
    new: "stage-new",
    engaged: "stage-engaged",
    warm: "stage-warm",
    hot: "stage-hot",
    payment_sent: "stage-payment_sent",
    paid: "stage-paid",
    lost: "stage-lost",
  };
  return map[stage] || "stage-new";
}

export default function Inbox() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [lead, setLead] = useState(null);
  const [text, setText] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const loadList = async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    if (!activeId && data.length) setActiveId(data[0].id);
  };

  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      const { data } = await api.get(`/conversations/${activeId}/messages`);
      setActive(data.conversation);
      setMsgs(data.messages);
      // fetch lead
      const leads = await api.get("/leads");
      setLead(leads.data.find((l) => l.id === data.conversation.lead_id));
    })();
  }, [activeId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeId) return;
    const { data } = await api.post(`/conversations/${activeId}/reply`, { text });
    setMsgs((m) => [...m, data]);
    setText("");
    loadList();
  };

  const suggest = async () => {
    if (!activeId) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/ai-suggest`);
      setText(data.text);
      toast.success(`AI suggestion ready · ${Math.round(data.confidence * 100)}% confidence`);
    } catch (e) {
      toast.error("AI is warming up — try again");
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="inbox-page">
      <div className="grid lg:grid-cols-12 h-[calc(100vh-0px)]">
        {/* List */}
        <div className="lg:col-span-3 border-r border-black/5 overflow-y-auto scrollbar-thin">
          <div className="p-5 border-b border-line sticky top-0 bg-white/95 backdrop-blur z-10">
            <h2 className="font-display text-lg font-bold text-ink">AI Inbox</h2>
            <p className="text-xs text-meta mt-1">Unified Instagram + WhatsApp</p>
          </div>
          <div>
            {convs.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                data-testid={`conv-${c.id}`}
                className={`w-full text-left px-5 py-3 border-b border-line transition ${activeId === c.id ? "bg-violet-50/60" : "hover:bg-zinc-50"}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full ashra-gradient-bg grid place-items-center text-xs font-bold text-white shrink-0">{c.lead_name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-ink truncate">{c.lead_name}</p>
                      {c.channel === "instagram" ? <Instagram className="h-3 w-3 text-meta" /> : <MessageCircle className="h-3 w-3 text-meta" />}
                    </div>
                    <p className="text-xs text-meta truncate">{c.last_message}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {c.unread > 0 && <span className="inline-flex items-center justify-center h-4 min-w-[16px] rounded-full ashra-gradient-bg text-[10px] text-white font-bold px-1">{c.unread}</span>}
                    <p className="text-[10px] text-success font-semibold mt-1">AI {Math.round(c.ai_confidence * 100)}%</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className="lg:col-span-6 flex flex-col border-r border-line">
          {active ? (
            <>
              <div className="p-5 border-b border-line flex items-center justify-between bg-white/60">
                <div>
                  <h3 className="font-display font-bold text-ink">{active.lead_name}</h3>
                  <p className="text-xs text-meta capitalize">{active.channel} · AI status: <span className="text-ai font-semibold">{active.ai_status}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="pill pill-ai"><Bot className="h-3 w-3" /> AI conf {Math.round(active.ai_confidence*100)}%</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-3 bg-zinc-50/40">
                {msgs.map((m) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${m.sender === "lead" ? "justify-start" : "justify-end"}`}
                  >
                    <div className={`bubble ${
                      m.sender === "lead" ? "bubble-lead" :
                      m.sender === "ai" ? "bubble-ai" :
                      "bubble-creator"
                    }`}>
                      <div className="text-ink-soft" style={m.sender === "creator" ? {color: "#FAFAFA"} : {}}>{m.text}</div>
                      {m.ai_confidence != null && (
                        <div className="bubble-ai-meta">
                          <Sparkles className="h-3 w-3 shrink-0" />
                          <span className="font-semibold">AI {Math.round(m.ai_confidence*100)}%</span>
                          <span className="opacity-70 truncate">· {m.ai_reasoning}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              <form onSubmit={send} className="p-4 border-t border-line flex items-center gap-2 bg-white">
                <button
                  type="button"
                  onClick={suggest}
                  disabled={suggesting}
                  className="rounded-full bg-ai-soft text-ai border border-violet-200 hover:bg-violet-100 px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 transition focus-ring"
                  data-testid="ai-suggest-btn"
                >
                  <Sparkles className="h-3 w-3" /> {suggesting ? "Thinking…" : "AI suggest"}
                </button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type your reply — or let AI suggest…"
                  className="flex-1 rounded-full bg-zinc-50 border border-line px-4 py-2 text-sm text-ink placeholder:text-meta-soft outline-none focus:border-violet-500 focus:bg-white transition"
                  data-testid="reply-input"
                />
                <button type="submit" className="rounded-full ashra-gradient-bg px-4 py-2 text-sm text-white font-semibold inline-flex items-center gap-1 shadow-sm hover:shadow-md transition" data-testid="reply-send">
                  <Send className="h-3.5 w-3.5" /> Send
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-meta">Select a conversation</div>
          )}
        </div>

        {/* Lead panel */}
        <div className="lg:col-span-3 overflow-y-auto scrollbar-thin p-5 bg-white">
          <h3 className="font-display font-bold text-ink mb-4">Lead Intelligence</h3>
          {lead ? (
            <div className="space-y-4">
              <div className="surface p-4">
                <p className="text-sm font-semibold text-ink">{lead.name}</p>
                <p className="text-xs text-meta">{lead.handle}</p>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className={`pill ${stageBadge(lead.stage)}`}>{lead.stage.replace("_", " ")}</span>
                  <span className="text-meta">Source: {lead.source}</span>
                </div>
              </div>
              <div className="surface p-4 space-y-3">
                <div>
                  <div className="flex justify-between text-xs"><span className="text-meta">Lead score</span><span className="text-ink font-semibold">{lead.score}</span></div>
                  <div className="h-1.5 rounded-full bg-zinc-100 mt-1.5 overflow-hidden"><div className="h-full rounded-full ashra-gradient-bg" style={{ width: `${lead.score*100}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-xs"><span className="text-meta">Intent</span><span className="text-ink font-semibold">{lead.intent_score}</span></div>
                  <div className="h-1.5 rounded-full bg-zinc-100 mt-1.5 overflow-hidden"><div className="h-full rounded-full bg-info" style={{ width: `${lead.intent_score*100}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-xs"><span className="text-meta">Purchase prob.</span><span className="text-ink font-semibold">{lead.purchase_probability}</span></div>
                  <div className="h-1.5 rounded-full bg-zinc-100 mt-1.5 overflow-hidden"><div className="h-full rounded-full bg-success" style={{ width: `${lead.purchase_probability*100}%` }} /></div>
                </div>
              </div>
              <div className="surface p-4">
                <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">Revenue potential</p>
                <p className="mt-1 font-display text-2xl font-bold text-success">${Math.round(lead.revenue_potential)}</p>
              </div>
              <div className="rounded-2xl border border-violet-200 bg-ai-soft p-4">
                <p className="text-xs text-ai font-bold inline-flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> AI reasoning</p>
                <p className="mt-1.5 text-xs text-ink-soft leading-relaxed">Pricing question + engaged with last 2 Reels. Likely to convert in 48h if offered split pay.</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-meta">No lead linked</p>
          )}
        </div>
      </div>
    </div>
  );
}
